import { Database, db } from '../database'
import {
  _getOptimization,
  _getOptimizations,
  _deleteOptimization,
  _hideOptimization,
  _unhideOptimization,
  _getOptimizationsSavings,
  _executeOptimization,
} from './optimization.requests'
import { CacheStateRepository, CacheStateTable } from '../cacheState.store'
import {
  deserializeOptimization,
  OptimizationTypes,
  serializeOptimization,
  createOptimizationTable,
  createOptimizationSavingsTable,
} from './optimization.models'
import { createUpdateSet, deepUnwrap } from '../util'
import dayjs from 'dayjs'
import {
  applyOptimizationFilters,
  OptimizationFilters,
} from './optimization.filter'
import { ExpressionBuilder, sql } from 'kysely'
import { SoftwareTypes } from '@/client'
import { SoftwareLicenseMini_Output } from '@/client'
import { usePreferenceStore } from '@/stores/preferenceStore'

const lastCacheChange = ref<Date>()
const lastSavingsCacheChange = ref<Date>()
const loadingFullReload = ref<boolean>(false)

type Pagination = {
  limit?: number
  offset?: number
}

type Sort = {
  column?: keyof OptimizationTypes
  direction?: 'asc' | 'desc'
}

export type EnrichedOptimizations = OptimizationTypes & {
  application_id: string
  application_display_name: string
  application_software_name: string
  application_type: SoftwareTypes | 'SSO' | null | undefined
  person_id: string
  person_name: string
  person_first_name: string
  person_last_name: string
  person_email: string
  savings: number
  licenses: Record<string, SoftwareLicenseMini_Output>
}

export const OptimizationStore = {
  loadingFullReload,
  lastCacheChange,
  lastSavingsCacheChange,

  async bulkInsertOptimizations(
    optimizations: OptimizationTypes[],
    batchSize: number = 1000
  ) {
    const serializedOptimizations = optimizations.map(serializeOptimization)

    await db.transaction().execute(async (trx) => {
      for (let i = 0; i < serializedOptimizations.length; i += batchSize) {
        const batch = serializedOptimizations.slice(i, i + batchSize)
        await trx
          .insertInto('optimization')
          .values(batch)
          .onConflict((oc) =>
            oc
              .column('_id')
              .doUpdateSet(createUpdateSet(serializedOptimizations[0]))
          )
          .execute()
      }
    })
    if (optimizations.length > 0) {
      lastCacheChange.value = new Date()
    }
    return optimizations
  },

  async getAllOptimizations(): Promise<OptimizationTypes[]> {
    const records = await db.selectFrom('optimization').selectAll().execute()
    return records.map(deserializeOptimization)
  },

  async getOptimizationsCount(
    filters: OptimizationFilters = { deprecated: false, hidden: false },
    join: boolean = false
  ): Promise<number> {
    let query = db.selectFrom('optimization')
    query = applyOptimizationFilters(query, filters)

    if (join) {
      query = query.innerJoin(
        'optimization_savings as os',
        'os._id',
        'optimization._id'
      )
    }

    const result = await query
      .select(({ fn }) => [fn.countAll().as('total')])
      .executeTakeFirst()

    return result ? Number(result.total) : 0
  },

  getDatabase() {
    return db.selectFrom('optimization')
  },

  async refreshOptimizationCacheFromApi(
    filter: OptimizationFilters = {},
    useServerCache: boolean = false,
    // useServerCache: boolean = false,
    includeDeleted: boolean = false
  ) {
    const results = await _getOptimizations({
      updatedGte: filter.updatedAfter,
      updatedLt: filter.updatedBefore,
      deletedAtGte: filter.deletedAfter,
      cached: useServerCache,
      includeDeleted: includeDeleted,
    })

    if (!results.error && results.data) {
      await this.bulkInsertOptimizations(results.data)
    }
    return results
  },

  async updateOptimizationCacheFromApi(fullReload: boolean = false) {
    type UpdateCacheStateTable = Partial<Omit<CacheStateTable, 'id'>> & {
      id: string
    }
    const filter: OptimizationFilters = {}
    const update: UpdateCacheStateTable = {
      id: 'optimizations',
      lastUpdate: dayjs().toISOString(),
    }
    const lastRefresh =
      await CacheStateRepository.getCacheStateById('optimizations')

    loadingFullReload.value = fullReload

    if (fullReload) {
      // Reload without backend cache
      await this.refreshOptimizationCacheFromApi(filter)
      update.lastCompleteLoad = dayjs().toISOString()
    } else if (lastRefresh && lastRefresh.lastUpdate) {
      // Load updated optimizations
      await this.refreshOptimizationCacheFromApi(
        {
          updatedAfter: lastRefresh.lastUpdate,
        },
        true, // use serverSide Cache
        false // includeDeleted
      )

      // Load deleted optimizations
      await this.refreshOptimizationCacheFromApi(
        {
          deletedAfter: lastRefresh.lastUpdate,
        },
        true, // use serverSide Cache
        true // includeDeleted
      )
    } else {
      // initial load
      await this.refreshOptimizationCacheFromApi(
        {},
        true, // use serverSide Cache
        true // includeDeleted
      )
    }

    await CacheStateRepository.upsertCacheState(update)
    loadingFullReload.value = false
  },

  async getOptimizations<T>(
    filters: OptimizationFilters = { hidden: false, deprecated: false },
    pagination: Pagination = {},
    sort: Sort = {},
    join: boolean = false
  ): Promise<T[]> {
    let query = db.selectFrom('optimization').selectAll()

    query = applyOptimizationFilters(query, filters)

    if (join) {
      // Join savings into the optimization
      query = query
        .leftJoin(
          'optimization_savings',
          'optimization_savings._id',
          'optimization._id'
        )
        .select(['optimization_savings.savings'])

      // Join person into the optimization
      query = query.leftJoin(
        (eb: ExpressionBuilder<Database, 'optimization'>) =>
          eb
            .selectFrom('person')
            .select([
              'email as person_email',
              'name as person_name',
              'first_name as person_first_name',
              'last_name as person_last_name',
              '_id as person_id',
            ])
            .as('person'),
        (join) =>
          join.on(
            sql.raw(
              `person.person_id = json_extract(optimization."references", '$.account_id')`
            )
          )
      )

      // Join application infos into the optimization
      query = query.leftJoin(
        (eb: ExpressionBuilder<Database, 'optimization'>) =>
          eb
            .selectFrom('application')
            .select([
              'display_name as application_display_name',
              'software_name as application_software_name',
              '_id as application_id',
              'type as application_type',
            ])
            .as('application'),
        (join) =>
          join.on(
            sql.raw(
              `application.application_id = json_extract(optimization."references", '$.software_id')`
            )
          )
      )

      // Join account licenses into the optimization
      query = query.leftJoin(
        (eb: ExpressionBuilder<Database, 'optimization'>) =>
          eb
            .selectFrom('licence_out')
            .select(['_id as account_id', 'software_licenses as licenses'])
            .as('account'),
        (join) =>
          join.on(
            sql.raw(
              `account.account_id = json_extract(optimization."references", '$.software_account_id')`
            )
          )
      )

      query = query.select(sql<string>`optimization._id`.as('_id'))
    }

    if (pagination.limit) {
      query = query.limit(pagination.limit)
    }
    if (pagination.offset) {
      query = query.offset(pagination.offset)
    }
    if (sort.column) {
      query = query.orderBy(sort.column, sort.direction ?? 'asc')
    }

    const results = await query.execute()
    return results.map(deserializeOptimization) as T[]
  },

  async hideOptimization(id: string) {
    return _hideOptimization(id).then(async (response) => {
      if (!response.error && response.data) {
        await this.bulkInsertOptimizations([response.data])
      }
      return response
    })
  },

  async unhideOptimization(id: string) {
    return _unhideOptimization(id).then(async (response) => {
      if (!response.error && response.data) {
        await this.bulkInsertOptimizations([response.data])
      }
      return response
    })
  },

  async executeOptimization(id: string) {
    return _executeOptimization(id).then(async (response) => {
      return response
    })
  },

  async getOptimizationById(id: string): Promise<OptimizationTypes | null> {
    const record = await db
      .selectFrom('optimization')
      .selectAll()
      .where('optimization._id', '=', id)
      .executeTakeFirst()

    let result = record ? deserializeOptimization(record) : record

    if (!record) {
      _getOptimization(id).then((response) => {
        if (response.data) {
          result = response.data
          this.bulkInsertOptimizations([result])
        }
      })
    }

    return result || null
  },

  async deleteOptimization(id: string) {
    return _deleteOptimization(id).then(async (response) => {
      await db
        .deleteFrom('optimization')
        .where('optimization._id', '=', id)
        .execute()
      lastCacheChange.value = new Date()
      return response
    })
  },

  useOptimizationsCount(
    initialFilters: OptimizationFilters | Ref<OptimizationFilters> = {},
    join: boolean = false
  ) {
    const count = ref<number>(0)

    const fetchCount = async () => {
      const filters = deepUnwrap(initialFilters) as OptimizationFilters
      count.value = await this.getOptimizationsCount(filters, join)
    }

    const stopWatch = watch(
      [() => unref(initialFilters), lastCacheChange],
      async () => {
        await fetchCount()
      },
      { deep: true }
    )

    fetchCount()

    const updateParameters = (
      newFilters: Partial<OptimizationFilters> = {}
    ) => {
      const cloneAndMerge = <T extends object>(
        source: T | Ref<T>,
        updates: Partial<T>
      ): T => {
        if (isRef(source)) {
          return { ...source.value, ...updates }
        } else {
          return { ...source, ...updates }
        }
      }

      initialFilters = cloneAndMerge(initialFilters, newFilters)

      fetchCount()
    }

    return {
      count,
      updateParameters,
      unsubscribe: stopWatch,
      lastCacheUpdate: lastCacheChange,
    }
  },

  useOptimizations<T>(
    initialFilters:
      | OptimizationFilters
      | Ref<OptimizationFilters>
      | ComputedRef<OptimizationFilters> = {},
    initialPagination:
      | Pagination
      | Ref<Pagination>
      | ComputedRef<Pagination> = {},
    initialSort: Sort | Ref<Sort> | ComputedRef<Sort> = {},
    join: boolean = false
  ) {
    const data = ref<T[]>([])
    const isLoading = ref(true)

    const fetchData = async () => {
      const filters = deepUnwrap(initialFilters) as OptimizationFilters
      const pagination = deepUnwrap(initialPagination) as Pagination
      const sort = deepUnwrap(initialSort) as Sort
      data.value = await this.getOptimizations<T>(
        filters,
        pagination,
        sort,
        join
      )

      isLoading.value = false
    }

    const stopWatch = watch(
      [
        () => unref(initialFilters),
        () => unref(initialPagination),
        () => unref(initialSort),
        lastCacheChange,
      ],
      async () => {
        await fetchData()
      },
      { deep: true }
    )

    const updateParameters = (
      newFilters: Partial<OptimizationFilters> = {},
      newPagination: Partial<Pagination> = {},
      newSort: Partial<Sort> = {}
    ) => {
      const cloneAndMerge = <T extends object>(
        source: T | Ref<T>,
        updates: Partial<T>
      ): T => {
        if (isRef(source)) {
          return { ...source.value, ...updates }
        } else {
          return { ...source, ...updates }
        }
      }

      initialFilters = cloneAndMerge(initialFilters, newFilters)
      initialPagination = cloneAndMerge(initialPagination, newPagination)
      initialSort = cloneAndMerge(initialSort, newSort)

      fetchData()
    }

    fetchData()

    return {
      data,
      unsubscribe: stopWatch,
      updateParameters,
      lastCacheUpdate: lastCacheChange,
      loading: isLoading,
    }
  },

  // ----------------------------------------------------------------------
  // Savings
  // ----------------------------------------------------------------------

  useOptimizationsSavings() {
    const savings = ref<number>(0)

    const fetchSavings = async () => {
      savings.value = await this.getOptimizationsSavings()
    }

    const stopWatch = watch(
      [() => lastCacheChange, lastSavingsCacheChange],
      async () => {
        await fetchSavings()
      },
      { deep: true }
    )

    fetchSavings()

    return {
      savings,
      unsubscribe: stopWatch,
      lastCacheUpdate: lastSavingsCacheChange,
    }
  },

  async getOptimizationsSavings(): Promise<number> {
    const query = db.selectFrom('optimization_savings')

    const result = await query
      .select(db.fn.sum('optimization_savings.savings').as('totalSavings'))
      .executeTakeFirst()

    return result?.totalSavings ? Number(result.totalSavings) : 0
  },

  async updateSavingsCacheFromApi() {
    await this.refreshSavingsCacheFromApi()
    lastSavingsCacheChange.value = new Date()
  },

  async refreshSavingsCacheFromApi() {
    const preferenceStore = usePreferenceStore()
    const preferences = await preferenceStore.getPreferences()

    const results = await _getOptimizationsSavings({
      targetCurrencyCode: preferences.currency_code,
    })

    if (!results.error && results.data && typeof results.data === 'object') {
      await this.bulkInsertOptimizationsSavings(results.data)
    }

    return results
  },

  async bulkInsertOptimizationsSavings(savings: Record<string, number>) {
    if (!Object.keys(savings).length) return savings
    const savingsArray = Object.entries(savings).map(([id, saving]) => ({
      _id: id,
      savings: saving,
    }))

    if (savingsArray.length === 0) {
      return
    }

    await db.transaction().execute(async (trx) => {
      await trx
        .insertInto('optimization_savings')
        .values(savingsArray)
        .onConflict((oc) =>
          oc.column('_id').doUpdateSet({
            savings: sql`excluded.savings`,
          })
        )
        .execute()
    })

    return savings
  },

  async reset() {
    await db.schema.dropTable('optimization').ifExists().execute()
    await db.schema.dropTable('optimization_savings').ifExists().execute()

    await createOptimizationTable()
    await createOptimizationSavingsTable()

    lastSavingsCacheChange.value = undefined
    lastCacheChange.value = undefined
  },
}
